import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface ResultsDisplayProps {
  results: {
    usernames: string[];
    bios: string[];
  };
  mode: "username" | "bio" | "both";
}

const ResultsDisplay = ({ results, mode }: ResultsDisplayProps) => {
  const [copiedIndex, setCopiedIndex] = useState<{ type: string; index: number } | null>(null);
  const { toast } = useToast();

  const handleCopy = (text: string, type: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex({ type, index });
    toast({
      title: "Copied!",
      description: "Text copied to clipboard",
    });
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const showUsernames = mode === "username" || mode === "both";
  const showBios = mode === "bio" || mode === "both";

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="border-t border-border pt-6">
        <h3 className="text-xl font-semibold mb-4">Generated Results</h3>

        {showUsernames && results.usernames.length > 0 && (
          <div className="space-y-3 mb-6">
            <h4 className="text-lg font-medium text-primary">Usernames</h4>
            {results.usernames.map((username, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-accent/50 rounded-lg hover:bg-accent transition-colors"
              >
                <span className="font-medium text-foreground">{username}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(username, "username", index)}
                  className="shrink-0"
                >
                  {copiedIndex?.type === "username" && copiedIndex.index === index ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {showBios && results.bios.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-lg font-medium text-secondary">Bios</h4>
            {results.bios.map((bio, index) => (
              <div
                key={index}
                className="flex items-start justify-between p-4 bg-accent/50 rounded-lg hover:bg-accent transition-colors"
              >
                <span className="text-foreground flex-1 pr-4">{bio}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(bio, "bio", index)}
                  className="shrink-0"
                >
                  {copiedIndex?.type === "bio" && copiedIndex.index === index ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsDisplay;
